import { z } from 'zod';

import { fixture } from './fixtures';

export const NAVBAR_ITEM_IDS = ['one_on_ones', 'rewards', 'celebrations', 'leaderboard', 'incentives', 'team', 'goals', 'advice', 'recaps'] as const;

export type NavbarItemId = (typeof NAVBAR_ITEM_IDS)[number];
export const NavbarItemIdSchema = z.enum(NAVBAR_ITEM_IDS);

export const NAVBAR_CONFIGURATION_SOURCE = ['user', 'company', 'default'] as const;
export type NavbarConfigurationSource = (typeof NAVBAR_CONFIGURATION_SOURCE)[number];
export const NavbarConfigurationSourceSchema = z.enum(NAVBAR_CONFIGURATION_SOURCE);

export const NAVBAR_FIXED_ITEM_IDS = ['announcements', 'home', 'search'] as const;
export type NavbarFixedItemId = (typeof NAVBAR_FIXED_ITEM_IDS)[number];

export const NavbarSettings = z.object({
	mainNavItems: z.array(NavbarItemIdSchema),
	moreMenuItems: z.array(NavbarItemIdSchema),
});

export type NavbarSettings = z.infer<typeof NavbarSettings>;

export const NavbarPreferences = z.object({
	mainNavItems: z.array(NavbarItemIdSchema).nullable(),
	moreMenuItems: z.array(NavbarItemIdSchema).nullable(),
});

export type NavbarPreferences = z.infer<typeof NavbarPreferences>;

export const NavbarConfiguration = z.object({
	fixedItems: z.array(z.string()),
	mainNavItems: z.array(NavbarItemIdSchema),
	moreMenuItems: z.array(NavbarItemIdSchema),
	source: NavbarConfigurationSourceSchema,
	availableItems: z.array(NavbarItemIdSchema),
});

export type NavbarConfiguration = z.infer<typeof NavbarConfiguration>;

export const CompanyNavbarSettingsResponse = z.object({
	settings: NavbarSettings.nullable(),
	defaults: NavbarSettings,
	fixedItems: z.array(z.string()),
	fixedItemsNote: z.string(),
	availableItems: z.array(NavbarItemIdSchema),
});

export type CompanyNavbarSettingsResponse = z.infer<typeof CompanyNavbarSettingsResponse>;

export const ResetUserPreferencesResponse = z.object({
	mainNavItems: z.array(NavbarItemIdSchema),
	moreMenuItems: z.array(NavbarItemIdSchema),
	source: z.enum(['company', 'default']),
});

export type ResetUserPreferencesResponse = z.infer<typeof ResetUserPreferencesResponse>;

export const ResetCompanySettingsResponse = z.object({
	settings: z.null(),
	defaults: NavbarSettings,
	message: z.string(),
});

export type ResetCompanySettingsResponse = z.infer<typeof ResetCompanySettingsResponse>;

export const NAVBAR_ITEM_TRANSLATION_KEYS: Record<NavbarItemId, string> = {
	one_on_ones: 'links.oneOnOnes',
	goals: 'links.goals',
	recaps: 'links.recaps',
	team: 'links.team',
	rewards: 'links.rewards',
	incentives: 'links.incentives',
	leaderboard: 'links.leaderboard',
	celebrations: 'links.celebrations',
	advice: 'links.feedback',
};

export const navbarSettingsFixture = (overrides: Partial<NavbarSettings> = {}): NavbarSettings => ({
	mainNavItems: ['one_on_ones', 'rewards', 'celebrations', 'leaderboard', 'incentives', 'team'],
	moreMenuItems: ['goals', 'advice', 'recaps'],
	...overrides,
});

export const navbarConfigurationFixture = (overrides: Partial<NavbarConfiguration> = {}): NavbarConfiguration => ({
	fixedItems: [...NAVBAR_FIXED_ITEM_IDS],
	source: 'default',
	availableItems: [...NAVBAR_ITEM_IDS],
	...navbarSettingsFixture(),
	...overrides,
});

export const companyNavbarSettingsResponseFixture = (overrides: Partial<CompanyNavbarSettingsResponse> = {}): CompanyNavbarSettingsResponse => ({
	settings: null,
	defaults: navbarSettingsFixture(),
	fixedItems: [...NAVBAR_FIXED_ITEM_IDS],
	fixedItemsNote: fixture.description('fixed-items-note'),
	availableItems: [...NAVBAR_ITEM_IDS],
	...overrides,
});
